import React, { useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Avatar, Rating, ToggleButtonGroup,
    ToggleButton, Button, Tabs, Tab, Tooltip
} from '@mui/material';
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import IconButton from '@mui/material/IconButton';
import { useSelector, useDispatch } from 'react-redux';
import CircularLoading from '../components/CircularLoading';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import Grid from '@mui/material/Grid';
import { FirebaseContext, api } from 'common';
import { ref as databaseRef, onValue as onDatabaseValue } from 'firebase/database';
import { FONT_FAMILY } from 'common/sharedFunctions';
import { useNavigate } from 'react-router-dom';
import {
    formatAdminMoney, getAccountingSummary, withVtcSettlements,
    getEatCourierSettlement, getEatRestaurantSettlement
} from '../common/adminFinance';

// ─── SVG markers ─────────────────────────────────────────────────────────────

const DEFAULT_CAR_MARKER = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="58" height="42" viewBox="0 0 58 42"><rect x="9" y="15" width="40" height="14" rx="7" fill="#1976d2" stroke="#ffffff" stroke-width="3"/><path d="M17 15l6-8h13l6 8" fill="#42a5f5" stroke="#ffffff" stroke-width="3" stroke-linejoin="round"/><circle cx="19" cy="31" r="5" fill="#0d47a1" stroke="#ffffff" stroke-width="2"/><circle cx="40" cy="31" r="5" fill="#0d47a1" stroke="#ffffff" stroke-width="2"/><circle cx="30" cy="22" r="4" fill="#ffffff" opacity="0.9"/></svg>`)}`;
const GHOST_CAR_MARKER = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="58" height="42" viewBox="0 0 58 42"><rect x="9" y="15" width="40" height="14" rx="7" fill="#5b5fc7" stroke="#ffffff" stroke-width="3"/><path d="M17 15l6-8h13l6 8" fill="#7679dd" stroke="#ffffff" stroke-width="3" stroke-linejoin="round"/><circle cx="19" cy="31" r="5" fill="#21264f" stroke="#ffffff" stroke-width="2"/><circle cx="40" cy="31" r="5" fill="#21264f" stroke="#ffffff" stroke-width="2"/><circle cx="30" cy="22" r="4" fill="#ffffff" opacity="0.9"/></svg>`)}`;
const EAT_COURIER_MARKER = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"><circle cx="24" cy="24" r="22" fill="#d4af37" stroke="#111318" stroke-width="4"/><path fill="#111318" d="M16.5 30.8a5.2 5.2 0 1 1 0-10.4 5.2 5.2 0 0 1 0 10.4Zm15 0a5.2 5.2 0 1 1 0-10.4 5.2 5.2 0 0 1 0 10.4Zm-15-3a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4Zm15 0a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4Zm-4.9-4.1h-6.1l2.5-5.1h-3.1v-3h7.8l-2.4 5h3.1l-1.8 3.1Zm-6.3 0h-2.8l-3.2-6.3h3.3l1.7 3.4 1-2.2h3.3l-3.3 5.1Z"/></svg>`)}`;
const clusterSvg = (count) => `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="54" height="54" viewBox="0 0 54 54"><circle cx="27" cy="27" r="24" fill="#172033" stroke="#ffffff" stroke-width="4"/><circle cx="27" cy="27" r="17" fill="#1769aa"/><text x="27" y="32" text-anchor="middle" font-family="Arial" font-size="14" font-weight="800" fill="#ffffff">${count}</text></svg>`)}`;

// ─── Helpers carte ────────────────────────────────────────────────────────────

const getLocationPoint = (v = {}) => {
    const s = v.coords || v.location || v;
    const lat = Number(s.lat ?? s.latitude);
    const lng = Number(s.lng ?? s.longitude);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};
const getFreshTs = (e = {}) => Number(e.lastLocationUpdate || e.locationUpdatedAt || e.location?.lastLocationUpdate || 0);
// Un chauffeur/livreur qui ferme l'app ou perd le réseau sans repasser par le
// bouton "hors ligne" (pas de détection de déconnexion serveur) laisse son
// flag "actif" bloqué à true en base. On considère donc qu'il n'est plus
// réellement en ligne si aucun ping GPS n'est arrivé depuis ce délai.
const MAP_STALE_THRESHOLD_MS = 3 * 60 * 1000;
const isFreshPing = (ts) => Number(ts) > 0 && (Date.now() - Number(ts)) <= MAP_STALE_THRESHOLD_MS;
const getEatOrderForCourier = (orders, uid) => uid ? orders.find(o => o.courierId === uid && !['DELIVERED', 'CANCELLED'].includes(o.status)) : null;
const getEatTs = (o = {}) => Number(o.deliveredAt || o.paidAt || o.updatedAt || o.createdAt || 0);
const getEatPlatNet = (o = {}) => Number(o.pricing?.platformNet ?? o.pricing?.platformFee ?? 0);
const getEatRestNet = (o = {}) => {
    const p = o.pricing || {};
    if (Number.isFinite(Number(p.restaurantNet))) return Number(p.restaurantNet);
    if (Number.isFinite(Number(p.restaurantSubtotal))) return Number(p.restaurantSubtotal);
    return (o.items || []).reduce((t, i) => t + Number(i.restaurantPrice || i.basePrice || i.originalPrice || i.price || 0) * Number(i.qty || i.quantity || 1), 0);
};
const isApprovedCourier = (c = {}) => c.approved !== false && !['documents_required', 'pending_review', 'rejected'].includes(String(c.applicationStatus || '').toLowerCase());
const isLiveCourier = (c = {}) => Boolean(getLocationPoint(c) && isApprovedCourier(c) && c.active !== false && String(c.status || '').toUpperCase() !== 'OFFLINE');

const clusterMarkers = (markers, zoom = 10) => {
    const p = zoom >= 15 ? 4 : zoom >= 12 ? 3 : zoom >= 9 ? 2 : 1;
    const groups = {};
    markers.forEach(m => {
        const k = `${Number(m.lat).toFixed(p)}:${Number(m.lng).toFixed(p)}:${m.type}`;
        if (!groups[k]) groups[k] = [];
        groups[k].push(m);
    });
    return Object.values(groups).map(g => {
        if (g.length === 1) return g[0];
        return { id: `cluster-${g.map(m => m.id).join('-')}`, type: 'cluster', lat: g.reduce((t, m) => t + Number(m.lat), 0) / g.length, lng: g.reduce((t, m) => t + Number(m.lng), 0) / g.length, count: g.length, drivername: `${g.length} points`, carImage: clusterSvg(g.length), statusLabel: 'Zone dense' };
    });
};

// ─── Design tokens (monochrome) ───────────────────────────────────────────────

const ff = FONT_FAMILY;
const ink = '#111827';      // texte principal
const sub = '#6b7280';      // sous-texte
const dim = '#9ca3af';      // très discret
const brd = '#e5e7eb';      // bordure
const bg  = '#f9fafb';      // fond page
const wh  = '#ffffff';      // fond carte

// ─── Atomes UI ────────────────────────────────────────────────────────────────

/** Carte KPI minimaliste — fond blanc, chiffre bold, label gris */
const Kpi = ({ label, value, sub: subtitle, wide }) => (
    <Box sx={{
        p: { xs: '14px 16px', sm: '16px 20px' },
        bgcolor: wh,
        border: `1px solid ${brd}`,
        borderRadius: 1.5,
        height: '100%',
        minWidth: wide ? 160 : 0,
    }}>
        <Typography sx={{ fontFamily: ff, fontSize: 11, fontWeight: 600, color: dim, textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.5 }}>
            {label}
        </Typography>
        <Typography sx={{ fontFamily: ff, fontSize: { xs: 22, sm: 26 }, fontWeight: 800, color: ink, lineHeight: 1.1 }}>
            {value}
        </Typography>
        {subtitle && (
            <Typography sx={{ fontFamily: ff, fontSize: 11, color: sub, mt: 0.5 }}>
                {subtitle}
            </Typography>
        )}
    </Box>
);

/** Section title */
const Ttl = ({ children }) => (
    <Typography sx={{ fontFamily: ff, fontSize: 12, fontWeight: 700, color: dim, textTransform: 'uppercase', letterSpacing: 0.6, mb: 1.5 }}>
        {children}
    </Typography>
);

/** Card wrapper */
const Card = ({ children, sx }) => (
    <Box sx={{ bgcolor: wh, border: `1px solid ${brd}`, borderRadius: 1.5, p: { xs: 2, sm: 2.5 }, ...sx }}>
        {children}
    </Box>
);

/** Table cell styles */
const th = { fontFamily: ff, fontSize: 11, fontWeight: 700, color: dim, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: `1px solid ${brd}`, py: 1, px: 1.5, bgcolor: 'transparent' };
const td = { fontFamily: ff, fontSize: 13, color: ink, borderBottom: `1px solid #f3f4f6`, py: 1.25, px: 1.5 };

/** Pastille de statut */
const Dot = ({ color = '#22c55e' }) => (
    <Box component="span" sx={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', bgcolor: color, mr: 0.75, flexShrink: 0 }} />
);

/**
 * Carte temps réel, mémoïsée.
 * IMPORTANT : ce composant doit rester défini au niveau du module (jamais
 * recréé à l'intérieur du rendu de Dashboard). Une fonction de composant
 * redéfinie à chaque rendu est traitée par React comme un TYPE différent :
 * l'instance <GoogleMap> précédente (avec sa position/zoom courants) est
 * alors intégralement détruite et reconstruite à chaque changement d'état
 * du Dashboard (ex: un ping GPS), même totalement indépendant de la carte —
 * c'est ce qui causait le clignotement/recentrage permanent constaté.
 * React.memo empêche en plus tout rendu si aucune prop pertinente n'a
 * changé (ex: mise à jour d'une statistique financière ailleurs sur la page).
 */
const DriverMap = React.memo(function DriverMap({
    mylocation, clustered, busiestZone, selectedMapPoint, onSelectMarker,
    mapRef, hasAutoCenteredRef, setMapZoom, getMarkerIcon,
    liveVtcCount, liveEatCount, ghostCount,
}) {
    const recenter = () => {
        if (!mapRef.current) return;
        const target = busiestZone || mylocation;
        if (!target) return;
        mapRef.current.panTo({ lat: target.lat, lng: target.lng });
        mapRef.current.setZoom(busiestZone?.count > 1 ? 11 : 10);
    };

    return (
        <Box sx={{ borderRadius: 1.5, overflow: 'hidden', border: `1px solid ${brd}`, bgcolor: wh }}>
            <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 2, borderBottom: `1px solid ${brd}`, flexWrap: 'wrap' }}>
                <Typography sx={{ fontFamily: ff, fontWeight: 700, fontSize: 13, color: ink }}>Carte temps réel</Typography>
                <Box sx={{ display: 'flex', gap: 2, ml: 'auto', flexWrap: 'wrap' }}>
                    <Typography sx={{ fontFamily: ff, fontSize: 12, color: sub, display: 'flex', alignItems: 'center' }}><Dot color="#2563eb" />{liveVtcCount} VTC</Typography>
                    <Typography sx={{ fontFamily: ff, fontSize: 12, color: sub, display: 'flex', alignItems: 'center' }}><Dot color="#d4af37" />{liveEatCount} Eats</Typography>
                    {ghostCount > 0 && <Typography sx={{ fontFamily: ff, fontSize: 12, color: sub, display: 'flex', alignItems: 'center' }}><Dot color="#9ca3af" />{ghostCount} sim.</Typography>}
                </Box>
            </Box>
            {mylocation?.lat ? (
                <Box sx={{ position: 'relative' }}>
                    <GoogleMap zoom={10} center={mylocation}
                        onLoad={m => {
                            mapRef.current = m;
                            setMapZoom(m.getZoom() || 10);
                            // Centrage initial unique sur la zone la plus dense si déjà connue au
                            // moment du montage — sinon un effet séparé le fera dès qu'elle arrive.
                            if (!hasAutoCenteredRef.current && busiestZone) {
                                hasAutoCenteredRef.current = true;
                                m.panTo({ lat: busiestZone.lat, lng: busiestZone.lng });
                                m.setZoom(busiestZone.count > 1 ? 11 : 10);
                            }
                        }}
                        onZoomChanged={() => mapRef.current && setMapZoom(mapRef.current.getZoom() || 10)}
                        onDragStart={() => { hasAutoCenteredRef.current = true; }}
                        onClick={() => onSelectMarker(null)}
                        mapContainerStyle={{ height: 'clamp(380px, 50vh, 600px)', width: '100%' }}
                    >
                        {clustered.map(marker => {
                            const statusColor = marker.type === 'cluster' ? '#2563eb' : marker.type === 'ghostDriver' ? '#6b7280' : marker.type === 'eatCourier' ? (marker.bookingRef ? '#d97706' : '#d4af37') : (marker.bookingRef ? '#d97706' : '#d4af37');
                            return (
                                <Marker key={marker.id} position={{ lat: marker.lat, lng: marker.lng }}
                                    icon={getMarkerIcon(marker)}
                                    onClick={() => { onSelectMarker(marker); if (marker.type === 'cluster' && mapRef.current) { mapRef.current.panTo({ lat: marker.lat, lng: marker.lng }); mapRef.current.setZoom(Math.min((mapRef.current.getZoom() || 10) + 2, 18)); } }}
                                >
                                    {selectedMapPoint?.id === marker.id && (
                                        <InfoWindow position={{ lat: marker.lat, lng: marker.lng }} options={{ pixelOffset: new window.google.maps.Size(0, -32) }} onCloseClick={() => onSelectMarker(null)}>
                                            <div style={{ fontFamily: ff, fontSize: 13, lineHeight: 1.6, minWidth: 160 }}>
                                                {marker.type === 'cluster' ? <><strong>{marker.count} proches</strong><br /><span style={{ color: '#6b7280', fontSize: 12 }}>Zoomez pour détailler</span></> : (
                                                    <>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}><strong>{marker.drivername}</strong><span style={{ width: 7, height: 7, borderRadius: '50%', background: statusColor, display: 'inline-block' }} /></div>
                                                        <div style={{ color: '#374151' }}>{marker.carnumber}</div>
                                                        <div style={{ color: '#6b7280', fontSize: 12 }}>{marker.cartype}{marker.city ? ` · ${marker.city}` : ''}</div>
                                                        <div style={{ color: '#111', fontWeight: 700, fontSize: 12, marginTop: 4 }}>{marker.statusLabel || (marker.bookingRef ? 'En course' : 'Disponible')}</div>
                                                        {marker.bookingRef && <div style={{ color: '#6b7280', fontSize: 11 }}>{marker.restaurantName ? `${marker.restaurantName} · ` : ''}Réf {marker.bookingRef}</div>}
                                                        <div style={{ color: '#9ca3af', fontSize: 11, marginTop: 2 }}>{new Date(marker.lastUpdate).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                                                    </>
                                                )}
                                            </div>
                                        </InfoWindow>
                                    )}
                                </Marker>
                            );
                        })}
                    </GoogleMap>
                    <Tooltip title="Recentrer sur la zone la plus active">
                        <IconButton
                            onClick={recenter}
                            sx={{
                                position: 'absolute', bottom: 12, right: 12, bgcolor: wh,
                                border: `1px solid ${brd}`, boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                '&:hover': { bgcolor: '#f3f4f6' }
                            }}
                        >
                            <MyLocationIcon sx={{ color: ink, fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            ) : (
                <Box sx={{ height: 380, display: 'flex', alignItems: 'center', justifyContent: 'center', color: dim, fontFamily: ff, fontSize: 13 }}>Chargement de la carte…</Box>
            )}
        </Box>
    );
});

// ─── Dashboard ────────────────────────────────────────────────────────────────

const Dashboard = () => {
    const navigate = useNavigate();
    const firebase = useContext(FirebaseContext);
    const mapRef = useRef(null);
    // Cache des icônes de marqueurs : @react-google-maps/api ré-applique
    // l'icône à chaque fois que l'objet passé en prop change de référence.
    // Sans cache, un objet {url, scaledSize} neuf était recréé à CHAQUE
    // rendu (déclenché en continu par les pings GPS temps réel), causant un
    // clignotement permanent des marqueurs sur la carte.
    const iconCacheRef = useRef({});
    // useCallback (deps vides) : référence stable indispensable pour que
    // React.memo sur <DriverMap> soit réellement efficace — une fonction
    // recréée à chaque rendu de Dashboard, passée en prop, casserait la
    // mémoïsation du composant carte.
    const getMarkerIcon = useCallback((marker) => {
        const [w, h] = marker.type === 'cluster' ? [48, 48] : marker.type === 'eatCourier' ? [36, 36] : marker.type === 'ghostDriver' ? [44, 32] : [35, 25];
        const key = `${marker.carImage}|${w}x${h}`;
        if (!iconCacheRef.current[key]) {
            iconCacheRef.current[key] = { url: marker.carImage, scaledSize: new window.google.maps.Size(w, h) };
        }
        return iconCacheRef.current[key];
    }, []);
    // Centre initial figé une seule fois (zone la plus dense de chauffeurs
    // actifs si connue, sinon position de l'admin) — n'est plus jamais
    // réappliqué automatiquement ensuite : l'admin est libre de se balader
    // sur la carte, un bouton dédié permet de recentrer manuellement.
    const hasAutoCenteredRef = useRef(false);
    const [mylocation, setMylocation] = useState(null);
    const [locations, setLocations] = useState([]);
    const [liveLocations, setLiveLocations] = useState({});
    const [liveDrivers, setLiveDrivers] = useState([]);
    const [ghostDrivers, setGhostDrivers] = useState([]);
    // Force une réévaluation périodique de la fraîcheur des marqueurs (un
    // chauffeur dont l'app est tuée n'émet plus AUCUNE écriture Firebase :
    // sans ce tick, son marqueur resterait affiché indéfiniment tant que
    // rien d'autre ne déclenche un recalcul).
    const [staleTick, setStaleTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setStaleTick(t => t + 1), 30 * 1000);
        return () => clearInterval(id);
    }, []);
    const [selectedMapPoint, setSelectedMapPoint] = useState(null);
    const [mapZoom, setMapZoom] = useState(10);
    const [dailygross, setDailygross] = useState(0);
    const [monthlygross, setMonthlygross] = useState(0);
    const [totalgross, setTotalgross] = useState(0);
    const [settings, setSettings] = useState({});
    const [tab, setTab] = useState(0);
    const { t } = useTranslation();
    const usersdata = useSelector(s => s.usersdata);
    const bookinglistdata = useSelector(s => s.bookinglistdata);
    const settingsdata = useSelector(s => s.settingsdata);
    const eatdata = useSelector(s => s.eatdata);
    const auth = useSelector(s => s.auth);
    const cars = useSelector(s => s.cartypes.cars);
    const [allCompleteCount, setAllCompleteCount] = useState([]);
    const [allCancelCount, setAllCancelCount] = useState([]);
    const [activeCount, setActiveCount] = useState(0);
    const [driverCount, setDriverCount] = useState(0);
    const [customerCount, setCustomerCount] = useState(0);
    const [topDrivers, setTopDrivers] = useState([]);
    const [timePeriod, setTimePeriod] = useState('month');
    const dispatch = useDispatch();
    const { fetchDrivers, clearFetchDrivers, fetchEatRestaurants, fetchEatOrders, fetchEatCouriers } = api;

    const dec = Number.isFinite(Number(settings.decimal)) ? Number(settings.decimal) : 2;
    const country = settings.country || 'France';
    const sym = settings.symbol || '€';
    const symAfter = settings.swipe_symbol !== false;
    const fmt = (v) => {
        const n = parseFloat(v || 0).toLocaleString(country === 'Vietnam' ? 'vi-VN' : 'en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
        return symAfter ? `${n} ${sym}` : `${sym} ${n}`;
    };

    useEffect(() => { dispatch(fetchDrivers('web')); return () => dispatch(clearFetchDrivers()); }, [dispatch, fetchDrivers, clearFetchDrivers]);
    useEffect(() => { if (auth.profile?.usertype === 'admin') { dispatch(fetchEatRestaurants()); dispatch(fetchEatOrders()); dispatch(fetchEatCouriers()); } }, [dispatch, auth.profile, fetchEatRestaurants, fetchEatOrders, fetchEatCouriers]);
    useEffect(() => {
        if (!firebase?.database || !['admin', 'fleetadmin'].includes(auth.profile?.usertype)) return;
        return onDatabaseValue(databaseRef(firebase.database, 'ghostDrivers'), snap => {
            setGhostDrivers(Object.entries(snap.val() || {}).map(([id, v]) => ({ id, ...v })).filter(d => d.active !== false && d.visibleOnDashboard !== false && getLocationPoint(d.location)));
        });
    }, [firebase, auth.profile]);

    // Listener temps réel sur /locations — se déclenche à chaque mise à jour GPS d'un chauffeur/livreur
    useEffect(() => {
        if (!firebase?.allLocationsRef) return;
        return onDatabaseValue(firebase.allLocationsRef, snap => {
            setLiveLocations(snap.val() || {});
        });
    }, [firebase]);

    // Listener temps réel dédié à la carte : indépendant du réglage admin
    // "realtime_drivers" (qui peut figer usersdata.drivers après un seul
    // fetch ailleurs dans l'app) — un chauffeur qui passe hors ligne doit
    // disparaître immédiatement de la carte, pas seulement au reload.
    useEffect(() => {
        if (!firebase?.driversRef) return;
        return onDatabaseValue(firebase.driversRef, snap => {
            const data = snap.val() || {};
            const arr = Object.keys(data)
                .filter(uid => data[uid].approved === true && data[uid].driverActiveStatus === true)
                .map(uid => ({
                    uid,
                    id: uid,
                    firstName: data[uid].firstName,
                    lastName: data[uid].lastName,
                    carType: data[uid].carType || null,
                    vehicleNumber: data[uid].vehicleNumber || null,
                    fleetadmin: data[uid].fleetadmin || null,
                    location: data[uid].location || null,
                }));
            setLiveDrivers(arr);
        });
    }, [firebase]);

    useEffect(() => { if (!mylocation) navigator.geolocation.getCurrentPosition(p => setMylocation({ lat: p.coords.latitude, lng: p.coords.longitude }), () => {}); }, [mylocation]);
    useEffect(() => { if (settingsdata.settings) setSettings(settingsdata.settings); }, [settingsdata.settings]);

    useEffect(() => {
        if (!bookinglistdata.bookings) return;
        const liveBookings = bookinglistdata.bookings.filter(b => b.status === 'STARTED');
        const eatCouriers = eatdata.couriers || [];
        const eatOrders = eatdata.orders || [];
        let locs = [];
        liveDrivers.forEach((d, i) => {
            // Priorité : position temps réel depuis /locations, fallback sur le profil driver
            const uid = d.uid || d.id;
            const freshLoc = liveLocations[uid] || d.location;
            if (!freshLoc?.lat || !freshLoc?.lng) return;
            const pingTs = freshLoc.lastLocationUpdate || freshLoc.locationUpdatedAt;
            if (!isFreshPing(pingTs)) return; // dernier ping GPS trop vieux -> app fermée/déconnectée
            let carImage = DEFAULT_CAR_MARKER;
            let bookingRef = null;
            cars.forEach(c => { if (c.name === d.carType) carImage = c.image; });
            liveBookings.forEach(b => { if (b.driver === uid) bookingRef = b.reference; });
            locs.push({ id: `driver-${uid || i}`, type: 'driver', lat: freshLoc.lat, lng: freshLoc.lng, drivername: `${d.firstName} ${d.lastName}`, carnumber: d.vehicleNumber, cartype: d.carType, carImage, bookingRef, fleetadmin: d.fleetadmin, lastUpdate: pingTs });
        });
        if (auth.profile.usertype === 'admin') {
            eatCouriers.forEach((c, i) => {
                const uid = c.uid || c.id;
                // Positions couriers depuis /locations en priorité
                const freshLoc = liveLocations[uid];
                const pt = freshLoc ? { lat: Number(freshLoc.lat), lng: Number(freshLoc.lng) } : getLocationPoint(c);
                if (!pt?.lat || !pt?.lng || !isLiveCourier(c)) return;
                const pingTs = freshLoc?.lastLocationUpdate || getFreshTs(c);
                if (!isFreshPing(pingTs)) return; // dernier ping GPS trop vieux -> app fermée/déconnectée
                const order = getEatOrderForCourier(eatOrders, uid);
                locs.push({ id: `eat-${uid || i}`, type: 'eatCourier', lat: pt.lat, lng: pt.lng, drivername: `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Livreur Eats', carnumber: c.mobile || 'Vélo', cartype: c.transportMode || 'Vélo Eats', carImage: EAT_COURIER_MARKER, bookingRef: order ? order.reference || order.id : null, fleetadmin: null, lastUpdate: pingTs, statusLabel: order ? 'En livraison' : 'Disponible', restaurantName: order?.restaurantName || null });
            });
        }
        if (auth.profile.usertype === 'fleetadmin') locs = locs.filter(l => l.fleetadmin === auth.profile.uid);
        setLocations(locs);
    }, [liveDrivers, liveLocations, auth.profile.usertype, auth.profile.uid, cars, bookinglistdata.bookings, eatdata.couriers, eatdata.orders, staleTick]);

    useEffect(() => {
        if (!bookinglistdata.bookings) return;
        const today = new Date();
        let daily = 0, monthly = 0, yearly = 0;
        bookinglistdata.bookings.forEach(b => {
            if (!['PAID', 'COMPLETE'].includes(b.status)) return;
            const ok = auth.profile.usertype === 'admin' || (auth.profile.usertype === 'fleetadmin' && b.fleetadmin === auth.profile.uid);
            if (!ok) return;
            const d = new Date(b.tripdate);
            const fee = auth.profile.usertype === 'admin' ? (parseFloat(b.convenience_fees || 0) - parseFloat(b.discount || 0)) : parseFloat(b.fleetCommission || 0);
            if (fee <= 0) return;
            if (d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate()) daily += fee;
            if (d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth()) monthly += fee;
            yearly += fee;
        });
        setDailygross(daily.toFixed(dec));
        setMonthlygross(monthly.toFixed(dec));
        setTotalgross(yearly.toFixed(dec));
    }, [bookinglistdata.bookings, dec, auth.profile.uid, auth.profile.usertype]);

    const eatMetrics = useMemo(() => {
        const orders = eatdata.orders || [], restaurants = eatdata.restaurants || [], couriers = eatdata.couriers || [];
        const today = new Date();
        const isRev = o => ['DELIVERED', 'PAID', 'COMPLETE', 'COMPLETED'].includes(String(o.status || '').toUpperCase()) || String(o.payment?.status || '').toUpperCase() === 'PAID';
        const sameDay = ts => { const d = new Date(ts); return d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear(); };
        const sameMonth = ts => { const d = new Date(ts); return d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear(); };
        const sameYear = ts => new Date(ts).getFullYear() === today.getFullYear();
        const rev = orders.filter(isRev);
        const commission = rev.reduce((t, o) => t + getEatPlatNet(o), 0);
        const dailyCommission = rev.reduce((t, o) => sameDay(getEatTs(o)) ? t + getEatPlatNet(o) : t, 0);
        const monthlyCommission = rev.reduce((t, o) => sameMonth(getEatTs(o)) ? t + getEatPlatNet(o) : t, 0);
        const restaurantRevenue = rev.reduce((acc, o) => { const a = getEatRestNet(o), ts = getEatTs(o); return { today: acc.today + (sameDay(ts) ? a : 0), month: acc.month + (sameMonth(ts) ? a : 0), year: acc.year + (sameYear(ts) ? a : 0), total: acc.total + a }; }, { today: 0, month: 0, year: 0, total: 0 });
        return {
            restaurants: restaurants.length, activeRestaurants: restaurants.filter(r => r.active !== false).length,
            orders: orders.length, pendingOrders: orders.filter(o => ['PLACED', 'PAID', 'RESTAURANT_ACCEPTED', 'READY_FOR_PICKUP'].includes(String(o.status || '').toUpperCase())).length,
            couriers: couriers.length, activeCouriers: couriers.filter(isLiveCourier).length,
            dailyCommission, monthlyCommission, commission, restaurantRevenue
        };
    }, [eatdata.orders, eatdata.restaurants, eatdata.couriers]);

    const combinedDaily = Number(dailygross || 0) + eatMetrics.dailyCommission;
    const combinedMonthly = Number(monthlygross || 0) + eatMetrics.monthlyCommission;
    const combinedTotal = Number(totalgross || 0) + eatMetrics.commission;

    const topEatRestaurants = useMemo(() => {
        const delivered = ['DELIVERED', 'PAID', 'COMPLETE', 'COMPLETED'];
        const stats = {};
        (eatdata.restaurants || []).forEach(r => { stats[r.id] = { id: r.id, name: r.name || 'Restaurant', active: r.active !== false && r.isOpen !== false, orders: 0, commission: 0, rating: Number(r.rating || 0) }; });
        (eatdata.orders || []).forEach(o => {
            const rid = o.restaurantId || o.restaurant_id;
            if (!rid) return;
            if (!stats[rid]) stats[rid] = { id: rid, name: o.restaurantName || rid, active: true, orders: 0, commission: 0, rating: 0 };
            const ok = delivered.includes(String(o.status || '').toUpperCase()) || String(o.payment?.status || '').toUpperCase() === 'PAID';
            if (ok) { stats[rid].orders++; stats[rid].commission += Number(o.pricing?.platformNet || o.pricing?.platformFee || 0); }
            const r = Number(o.restaurantRating || o.rating || 0);
            if (r > 0) stats[rid].rating = stats[rid].rating ? (stats[rid].rating + r) / 2 : r;
        });
        return Object.values(stats).sort((a, b) => b.orders - a.orders || b.commission - a.commission).slice(0, 8);
    }, [eatdata.restaurants, eatdata.orders]);

    const topEatCouriers = useMemo(() => {
        const delivered = ['DELIVERED', 'COMPLETE', 'COMPLETED'];
        const stats = {};
        (eatdata.couriers || []).forEach(c => { const id = c.uid || c.id; stats[id] = { id, name: `${c.firstName || ''} ${c.lastName || ''}`.trim() || id || 'Livreur', active: Boolean(c.active), deliveries: 0, earnings: 0, rating: Number(c.rating || 0) }; });
        (eatdata.orders || []).forEach(o => {
            const cid = o.courierId || o.courier || o.driver;
            if (!cid) return;
            if (!stats[cid]) stats[cid] = { id: cid, name: o.courierName || cid, active: false, deliveries: 0, earnings: 0, rating: 0 };
            if (delivered.includes(String(o.status || '').toUpperCase())) { stats[cid].deliveries++; stats[cid].earnings += Number(o.pricing?.courierEarning || 0); }
            const r = Number(o.courierRating || o.driverRating || 0);
            if (r > 0) stats[cid].rating = stats[cid].rating ? (stats[cid].rating + r) / 2 : r;
        });
        return Object.values(stats).sort((a, b) => b.deliveries - a.deliveries || b.earnings - a.earnings).slice(0, 8);
    }, [eatdata.couriers, eatdata.orders]);

    const liveVtc = useMemo(() => locations.filter(m => m.type === 'driver'), [locations]);
    const liveEat = useMemo(() => locations.filter(m => m.type === 'eatCourier'), [locations]);
    const ghostMarkers = useMemo(() => ghostDrivers.map(d => {
        const pt = getLocationPoint(d.location);
        return { id: `ghost-${d.id}`, type: 'ghostDriver', lat: pt.lat, lng: pt.lng, drivername: d.name || 'Simulation', carnumber: d.vehicleNumber || '', cartype: `${d.vehicleMake || ''} ${d.vehicleModel || ''}`.trim() || 'Confort', carImage: GHOST_CAR_MARKER, bookingRef: null, lastUpdate: d.location?.lastLocationUpdate || Date.now(), statusLabel: 'Simulation', city: d.city };
    }), [ghostDrivers]);
    const allMarkers = useMemo(() => [...locations, ...ghostMarkers], [locations, ghostMarkers]);
    const clustered = useMemo(() => clusterMarkers(allMarkers, mapZoom), [allMarkers, mapZoom]);
    // Zone géographique regroupant le plus de chauffeurs/livreurs actifs
    // (regroupement large, indépendant du zoom courant) — sert de point de
    // centrage initial et de cible au bouton de recentrage manuel.
    const busiestZone = useMemo(() => {
        // Uniquement les chauffeurs/livreurs RÉELS (jamais les simulations
        // "ghost", sinon la carte peut se centrer sur une zone de test où il
        // n'y a personne de réel) et seulement s'il existe un vrai
        // regroupement (>= 2) — un point isolé pris au hasard ne représente
        // pas "la zone la plus active", on garde alors le centre par défaut.
        if (!locations.length) return null;
        const wide = clusterMarkers(locations, 9); // précision large (p=2)
        const densest = [...wide].sort((a, b) => (b.count || 1) - (a.count || 1))[0];
        if (!densest || (densest.count || 1) < 2) return null;
        return { lat: densest.lat, lng: densest.lng, count: densest.count };
    }, [locations]);

    // Si la carte est déjà montée (mapRef prêt) avant que busiestZone ne soit
    // connue (données Firebase arrivées après la géolocalisation), on centre
    // dès qu'elle devient disponible — une seule fois, jamais après.
    useEffect(() => {
        if (hasAutoCenteredRef.current || !busiestZone || !mapRef.current) return;
        hasAutoCenteredRef.current = true;
        mapRef.current.panTo({ lat: busiestZone.lat, lng: busiestZone.lng });
        mapRef.current.setZoom(busiestZone.count > 1 ? 11 : 10);
    }, [busiestZone]);

    const activeEatOrders = useMemo(() => (eatdata.orders || [])
        .filter(o => ['PLACED', 'PAID', 'RESTAURANT_ACCEPTED', 'PREPARING', 'READY_FOR_PICKUP', 'COURIER_ASSIGNED', 'COURIER_ACCEPTED', 'PICKED_UP', 'ON_THE_WAY'].includes(String(o.status || '').toUpperCase()))
        .sort((a, b) => Number(b.updatedAt || b.createdAt || 0) - Number(a.updatedAt || a.createdAt || 0))
        .slice(0, 8), [eatdata.orders]);

    const accounting = useMemo(() => {
        const driverRows = withVtcSettlements((usersdata.users || []).filter(u => u.usertype === 'driver' && (auth.profile.usertype === 'admin' || u.fleetadmin === auth.profile.uid)), bookinglistdata.bookings || []);
        const courierRows = (eatdata.couriers || []).map(c => ({ ...c, platformOwesAmount: getEatCourierSettlement(eatdata.orders || [], c.uid || c.id).platformOwes }));
        const restaurantRows = (eatdata.restaurants || []).map(r => ({ ...r, platformOwesAmount: getEatRestaurantSettlement(eatdata.orders || [], r.id || r.restaurantId).platformOwes }));
        return getAccountingSummary(driverRows, courierRows, restaurantRows);
    }, [auth.profile.uid, auth.profile.usertype, bookinglistdata.bookings, eatdata.couriers, eatdata.orders, eatdata.restaurants, usersdata.users]);

    useEffect(() => {
        if (usersdata.users) {
            let dc = 0, ac = 0, cc = 0;
            usersdata.users.forEach(u => {
                const ok = auth.profile.usertype === 'admin' || (auth.profile.usertype === 'fleetadmin' && u.fleetadmin === auth.profile.uid);
                if (u.usertype === 'driver' && ok) dc++;
                if (u.driverActiveStatus === true && ok) ac++;
                if (u.usertype === 'customer' && ok) cc++;
            });
            setDriverCount(dc); setActiveCount(ac); setCustomerCount(cc);
        } else { setDriverCount(0); setActiveCount(0); setCustomerCount(0); }
    }, [usersdata.users, auth.profile]);

    useEffect(() => {
        let compl = new Array(12).fill(0), canc = new Array(12).fill(0);
        (bookinglistdata.bookings || []).forEach(b => {
            const m = new Date(b.tripdate).getMonth();
            if (b.status === 'PAID' || b.status === 'COMPLETE') compl[m]++;
            if (b.status === 'CANCELLED') canc[m]++;
        });
        setAllCompleteCount(compl); setAllCancelCount(canc);
    }, [bookinglistdata.bookings]);

    useEffect(() => {
        if (!usersdata.users || !bookinglistdata.bookings) return;
        const today = new Date(), now = Date.now(), ONE_MONTH = 30 * 24 * 60 * 60 * 1000;
        const stats = usersdata.users.filter(u => u.usertype === 'driver').map(driver => {
            let bookings = bookinglistdata.bookings.filter(b => b.driver === driver.id && (b.status === 'COMPLETE' || b.status === 'PAID'));
            if (timePeriod === 'day') bookings = bookings.filter(b => { const d = new Date(b.tripdate); return d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear(); });
            else if (timePeriod === 'month') bookings = bookings.filter(b => { const d = new Date(b.tripdate); return d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear(); });
            else if (timePeriod === 'year') bookings = bookings.filter(b => new Date(b.tripdate).getFullYear() === today.getFullYear());
            const totalRides = bookings.length;
            const ratings = bookings.filter(b => b.rating > 0).map(b => parseFloat(b.rating));
            const avgRating = ratings.length ? ratings.reduce((a, b) => a + b, 0) / ratings.length : 0;
            let activeTime = 0;
            if (driver.location?.lastLocationUpdate) {
                const age = now - (driver.createdAt || now);
                activeTime = (age < ONE_MONTH ? Math.min(age / 3600000, totalRides * 2) : Math.min(ONE_MONTH / 3600000, totalRides * 2.5)) * (driver.driverActiveStatus ? 1.2 : 1);
            }
            const score = (Math.min(avgRating / 5, 1) * 0.4 + Math.min(totalRides / 100, 1) * 0.3 + Math.min(activeTime / 200, 1) * 0.3) * 5;
            const firstName = driver.firstName || (driver.name ? driver.name.split(' ')[0] : '') || '';
            const lastName = driver.lastName || (driver.name ? driver.name.split(' ').slice(1).join(' ') : '') || '';
            const displayName = `${firstName} ${lastName}`.trim() || driver.mobile || driver.email || driver.id || 'Chauffeur';
            return { uid: driver.id, firstName, lastName, displayName, profile_image: driver.profile_image, totalRides, avgRating: parseFloat(avgRating.toFixed(1)), personalScore: parseFloat(score.toFixed(1)), carType: driver.carType, vehicleNumber: driver.vehicleNumber };
        }).filter(d => d.totalRides > 0).sort((a, b) => b.personalScore - a.personalScore).slice(0, 10);
        setTopDrivers(stats);
    }, [usersdata.users, bookinglistdata.bookings, timePeriod]);

    const chartOpts = {
        chart: { type: 'area', fontFamily: ff, toolbar: { show: false }, sparkline: { enabled: false } },
        colors: ['#111827', '#9ca3af'],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: [2, 2] },
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.15, opacityTo: 0.02 } },
        grid: { borderColor: '#f3f4f6', strokeDashArray: 4, xaxis: { lines: { show: false } } },
        xaxis: { categories: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'], labels: { style: { fontFamily: ff, fontSize: '11px', colors: Array(12).fill(sub) } }, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { labels: { style: { fontFamily: ff, fontSize: '11px', colors: [sub] } } },
        legend: { fontFamily: ff, fontSize: '12px', offsetY: 4 },
        tooltip: { style: { fontFamily: ff } }
    };

    if (bookinglistdata.loading || usersdata.loading) return <CircularLoading />;

    return (
        <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, bgcolor: bg, minHeight: '100vh' }}>

            {/* ── Onglets ── */}
            <Box sx={{ borderBottom: `1px solid ${brd}`, mb: 3 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
                    TabIndicatorProps={{ style: { backgroundColor: ink, height: 2 } }}
                    sx={{ minHeight: 40, '& .MuiTab-root': { fontFamily: ff, fontWeight: 600, fontSize: 13, color: dim, textTransform: 'none', minWidth: 'auto', px: { xs: 2, sm: 2.5 }, py: 1, minHeight: 40 }, '& .Mui-selected': { color: `${ink} !important` } }}
                >
                    <Tab label="Vue générale" />
                    <Tab label="VTC" />
                    <Tab label="Eats" />
                    <Tab label="Comptabilité" />
                    <Tab label="Activité" />
                </Tabs>
            </Box>

            {/* ══════════════════════════════════════
                TAB 0 — Vue générale
            ═════════════════════════════════════════ */}
            {tab === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                    {/* Revenus */}
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <Kpi label="Aujourd'hui" value={fmt(combinedDaily)} sub={`VTC ${fmt(dailygross)} · Eats ${fmt(eatMetrics.dailyCommission)}`} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Kpi label="Ce mois" value={fmt(combinedMonthly)} sub={`VTC ${fmt(monthlygross)} · Eats ${fmt(eatMetrics.monthlyCommission)}`} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Kpi label="Total" value={fmt(combinedTotal)} sub={`VTC ${fmt(totalgross)} · Eats ${fmt(eatMetrics.commission)}`} />
                        </Grid>
                    </Grid>

                    {/* Carte + indicateurs */}
                    <Grid container spacing={2} alignItems="stretch">
                        <Grid item xs={12} lg={9}>
                            <DriverMap
                                mylocation={mylocation}
                                clustered={clustered}
                                busiestZone={busiestZone}
                                selectedMapPoint={selectedMapPoint}
                                onSelectMarker={setSelectedMapPoint}
                                mapRef={mapRef}
                                hasAutoCenteredRef={hasAutoCenteredRef}
                                setMapZoom={setMapZoom}
                                getMarkerIcon={getMarkerIcon}
                                liveVtcCount={liveVtc.length}
                                liveEatCount={liveEat.length}
                                ghostCount={ghostMarkers.length}
                            />
                        </Grid>
                        <Grid item xs={12} lg={3}>
                            <Grid container spacing={1.5} sx={{ height: '100%', alignContent: 'start' }}>
                                <Grid item xs={6} lg={12}><Kpi label="Clients" value={customerCount} sub="inscrits" /></Grid>
                                <Grid item xs={6} lg={12}><Kpi label="Chauffeurs VTC" value={driverCount} sub={`${activeCount} en ligne`} /></Grid>
                                <Grid item xs={6} lg={12}><Kpi label="Restaurants" value={eatMetrics.activeRestaurants} sub="actifs Eats" /></Grid>
                                <Grid item xs={6} lg={12}><Kpi label="Livreurs" value={liveEat.length} sub="en ligne Eats" /></Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* ══════════════════════════════════════
                TAB 1 — VTC
            ═════════════════════════════════════════ */}
            {tab === 1 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}><Kpi label="Chauffeurs inscrits" value={driverCount} /></Grid>
                        <Grid item xs={6} sm={3}><Kpi label="En ligne" value={activeCount} sub="connectés maintenant" /></Grid>
                        <Grid item xs={6} sm={3}><Kpi label="Sur la carte" value={liveVtc.length} sub="position GPS active" /></Grid>
                        <Grid item xs={6} sm={3}><Kpi label="Commission — ce mois" value={fmt(monthlygross)} /></Grid>
                    </Grid>

                    <Card>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                            <Ttl>Top chauffeurs</Ttl>
                            <ToggleButtonGroup value={timePeriod} exclusive onChange={(_, v) => v && setTimePeriod(v)} size="small"
                                sx={{ '& .MuiToggleButton-root': { fontFamily: ff, textTransform: 'none', px: 1.75, fontSize: 12, color: sub, borderColor: brd, py: 0.5, '&.Mui-selected': { color: ink, bgcolor: '#f9fafb', borderColor: brd } } }}
                            >
                                <ToggleButton value="day">Aujourd'hui</ToggleButton>
                                <ToggleButton value="month">Ce mois</ToggleButton>
                                <ToggleButton value="year">Cette année</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ ...th, width: 36 }}>#</TableCell>
                                        <TableCell sx={th}>Chauffeur</TableCell>
                                        <TableCell align="center" sx={th}>Score</TableCell>
                                        <TableCell align="center" sx={th}>Note</TableCell>
                                        <TableCell align="center" sx={th}>Courses</TableCell>
                                        <TableCell sx={{ ...th, display: { xs: 'none', md: 'table-cell' } }}>Véhicule</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {topDrivers.length > 0 ? topDrivers.map((d, i) => (
                                        <TableRow key={d.uid} sx={{ '&:hover': { bgcolor: bg } }}>
                                            <TableCell sx={{ ...td, color: dim, fontWeight: 700 }}>{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}</TableCell>
                                            <TableCell sx={td}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                                    <Avatar src={d.profile_image} sx={{ width: 28, height: 28, fontSize: 11 }} />
                                                    <Typography sx={{ fontFamily: ff, fontSize: 13, fontWeight: 600, color: ink }}>{d.displayName}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center" sx={{ ...td, fontWeight: 700 }}>{d.personalScore}/5</TableCell>
                                            <TableCell align="center" sx={td}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                    <Rating value={d.avgRating} precision={0.1} readOnly size="small" sx={{ '& .MuiRating-iconFilled': { color: '#111827' }, '& .MuiRating-iconEmpty': { color: brd } }} />
                                                    <Typography sx={{ fontFamily: ff, fontSize: 11, color: dim }}>{d.avgRating}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center" sx={{ ...td, fontWeight: 700 }}>{d.totalRides}</TableCell>
                                            <TableCell sx={{ ...td, display: { xs: 'none', md: 'table-cell' } }}>
                                                <Typography sx={{ fontFamily: ff, fontSize: 12, color: ink }}>{d.carType}</Typography>
                                                <Typography sx={{ fontFamily: ff, fontSize: 11, color: dim }}>{d.vehicleNumber}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5, color: dim, fontFamily: ff, fontSize: 13 }}>Aucune donnée sur cette période</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Typography sx={{ fontFamily: ff, fontSize: 11, color: dim, mt: 1.5 }}>Score = 40% note · 30% courses · 30% temps actif estimé</Typography>
                    </Card>
                </Box>
            )}

            {/* ══════════════════════════════════════
                TAB 2 — Eats
            ═════════════════════════════════════════ */}
            {tab === 2 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}><Kpi label="Restaurants actifs" value={eatMetrics.activeRestaurants} /></Grid>
                        <Grid item xs={6} sm={3}><Kpi label="Livreurs en ligne" value={liveEat.length} /></Grid>
                        <Grid item xs={6} sm={3}><Kpi label="Commandes en cours" value={eatMetrics.pendingOrders} /></Grid>
                        <Grid item xs={6} sm={3}><Kpi label="Commission — ce mois" value={fmt(eatMetrics.monthlyCommission)} /></Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        {/* Commandes + livreurs live */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{ height: '100%' }}>
                                <Ttl>Commandes en cours</Ttl>
                                {activeEatOrders.length > 0 ? activeEatOrders.map((o, i) => (
                                    <Box key={o.id || i} sx={{ py: 1.25, borderBottom: i < activeEatOrders.length - 1 ? `1px solid #f3f4f6` : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontFamily: ff, fontWeight: 600, fontSize: 13, color: ink }}>{o.restaurantName || 'Restaurant'}</Typography>
                                            <Typography sx={{ fontFamily: ff, fontSize: 11, color: sub }}>{o.courierName || 'À assigner'} · {o.reference || o.id}</Typography>
                                        </Box>
                                        <Typography sx={{ fontFamily: ff, fontSize: 11, color: dim, whiteSpace: 'nowrap', pt: 0.25 }}>{o.status}</Typography>
                                    </Box>
                                )) : <Typography sx={{ fontFamily: ff, color: dim, fontSize: 13, py: 1 }}>Aucune commande active.</Typography>}

                                {liveEat.length > 0 && (
                                    <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${brd}` }}>
                                        <Ttl>Livreurs en ligne</Ttl>
                                        {liveEat.slice(0, 6).map((c, i) => (
                                            <Box key={c.id} sx={{ py: 1, borderBottom: i < Math.min(liveEat.length, 6) - 1 ? `1px solid #f3f4f6` : 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Dot color={c.bookingRef ? '#d97706' : '#d4af37'} />
                                                    <Typography sx={{ fontFamily: ff, fontSize: 13, color: ink }}>{c.drivername}</Typography>
                                                </Box>
                                                <Typography sx={{ fontFamily: ff, fontSize: 11, color: dim }}>{c.bookingRef ? 'En livraison' : 'Dispo'}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Card>
                        </Grid>

                        {/* Top restaurants */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{ height: '100%' }}>
                                <Ttl>Top restaurants</Ttl>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ ...th, width: 28 }}>#</TableCell>
                                                <TableCell sx={th}>Restaurant</TableCell>
                                                <TableCell align="right" sx={th}>Cmd.</TableCell>
                                                <TableCell align="right" sx={th}>Comm.</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {topEatRestaurants.length > 0 ? topEatRestaurants.map((r, i) => (
                                                <TableRow key={r.id} sx={{ '&:hover': { bgcolor: bg } }}>
                                                    <TableCell sx={{ ...td, color: dim, fontWeight: 700 }}>#{i + 1}</TableCell>
                                                    <TableCell sx={td}>
                                                        <Typography sx={{ fontFamily: ff, fontWeight: 600, fontSize: 13 }}>{r.name}</Typography>
                                                        <Typography sx={{ fontFamily: ff, fontSize: 11, color: dim }}>{r.active ? 'Ouvert' : 'Fermé'}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ ...td, fontWeight: 700 }}>{r.orders}</TableCell>
                                                    <TableCell align="right" sx={{ ...td, fontWeight: 700 }}>{fmt(r.commission)}</TableCell>
                                                </TableRow>
                                            )) : <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: dim, fontFamily: ff, fontSize: 13 }}>Aucun restaurant</TableCell></TableRow>}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Card>
                        </Grid>

                        {/* Top livreurs */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{ height: '100%' }}>
                                <Ttl>Top livreurs</Ttl>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ ...th, width: 28 }}>#</TableCell>
                                                <TableCell sx={th}>Livreur</TableCell>
                                                <TableCell align="right" sx={th}>Livr.</TableCell>
                                                <TableCell align="right" sx={th}>Gains</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {topEatCouriers.length > 0 ? topEatCouriers.map((c, i) => (
                                                <TableRow key={c.id} sx={{ '&:hover': { bgcolor: bg } }}>
                                                    <TableCell sx={{ ...td, color: dim, fontWeight: 700 }}>#{i + 1}</TableCell>
                                                    <TableCell sx={td}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                            <Dot color={c.active ? '#d4af37' : '#9ca3af'} />
                                                            <Typography sx={{ fontFamily: ff, fontWeight: 600, fontSize: 13 }}>{c.name}</Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ ...td, fontWeight: 700 }}>{c.deliveries}</TableCell>
                                                    <TableCell align="right" sx={{ ...td, fontWeight: 700 }}>{fmt(c.earnings)}</TableCell>
                                                </TableRow>
                                            )) : <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: dim, fontFamily: ff, fontSize: 13 }}>Aucun livreur</TableCell></TableRow>}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* ══════════════════════════════════════
                TAB 3 — Comptabilité
            ═════════════════════════════════════════ */}
            {tab === 3 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={4} md={2.4}><Kpi label="Ils me doivent" value={formatAdminMoney(accounting.driverOwes, settings)} sub="commissions chauffeurs" /></Grid>
                        <Grid item xs={6} sm={4} md={2.4}><Kpi label="Je dois — chauffeurs" value={formatAdminMoney(accounting.platformOwesDrivers, settings)} sub="VTC à reverser" /></Grid>
                        <Grid item xs={6} sm={4} md={2.4}><Kpi label="Je dois — livreurs" value={formatAdminMoney(accounting.platformOwesCouriers, settings)} sub="Eats à reverser" /></Grid>
                        <Grid item xs={6} sm={4} md={2.4}><Kpi label="Je dois — restaurants" value={formatAdminMoney(accounting.platformOwesRestaurants, settings)} sub="net restaurants" /></Grid>
                        <Grid item xs={6} sm={4} md={2.4}><Kpi label="Soldes à traiter" value={accounting.attentionCount} sub="négatifs en attente" /></Grid>
                    </Grid>
                    <Card>
                        <Ttl>Accès rapides</Ttl>
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            {[['Chauffeurs VTC', '/users/1'], ['Livreurs Eats', '/users/4'], ['Restaurants', '/users/5']].map(([label, path]) => (
                                <Button key={path} variant="outlined" size="small" onClick={() => navigate(path)}
                                    sx={{ fontFamily: ff, textTransform: 'none', fontSize: 13, borderColor: brd, color: ink, '&:hover': { bgcolor: bg, borderColor: '#d1d5db' } }}>
                                    {label}
                                </Button>
                            ))}
                            <Button variant="outlined" size="small" onClick={() => navigate('/withdraws')}
                                sx={{ fontFamily: ff, textTransform: 'none', fontSize: 13, borderColor: ink, color: ink, fontWeight: 700, '&:hover': { bgcolor: '#f3f4f6' } }}>
                                Retraits
                            </Button>
                        </Box>
                    </Card>
                </Box>
            )}

            {/* ══════════════════════════════════════
                TAB 4 — Activité
            ═════════════════════════════════════════ */}
            {tab === 4 && (
                <Card>
                    <Ttl>Courses — {new Date().getFullYear()}</Ttl>
                    <Chart options={chartOpts} series={[{ name: t('completed_bookings'), data: allCompleteCount }, { name: t('cancelled_bookings'), data: allCancelCount }]} type="area" width="100%" height={340} />
                </Card>
            )}

        </Box>
    );
};

export default Dashboard;
